'use client';

import { FC, ReactNode } from 'react';
import clsx from 'clsx';
import SpinIcon from '@/assets/loading-icon.svg';
import Image from 'next/image';

interface IButtonProps {
	variant: 'outline' | 'contained';
	environment: 'light' | 'dark';
	children: ReactNode;
	isSubmit?: boolean;
	selected?: boolean;
	classOverrides?: string;
	loading?: boolean;
	onClick?: () => void;
	disabled?: boolean;
}

const Button: FC<IButtonProps> = ({
	children,
	variant,
	isSubmit,
	onClick,
	environment,
	classOverrides,
	selected,
	disabled = false,
	loading,
}) => {
	const lightStyles = `bg-white-000 text-grey-700 hover:bg-grey-50 ${selected ? '!text-grey-900 !bg-grey-200' : ''}`;
	const darkStyles = `bg-grey-800 text-secondary-light hover:bg-grey-600 hover:text-primary-light ${selected ? 'bg-grey-700 outline outline-2 outline-offset-[-2px] outline-grey-50' : ''}`;
	const disabledStyles = '!text-grey-200 !bg-grey-400 pointer-events-none';

	const getOutlineStyles = () => {
		const baseStyles = 'bg-transparent border-2';
		switch (environment) {
			case 'light':
				return `${baseStyles} text-grey-900 border-grey-700 hover:border-grey-600`;
			case 'dark':
				return `${baseStyles} text-secondary-light border-grey-500 hover:border-grey-400`;
			default:
				return `${baseStyles} border-grey-500 hover:border-grey-400`;
		}
	};

	const getVariantStyles = () => {
		if (disabled) return `${disabledStyles}`;

		switch (variant) {
			case 'outline':
				return getOutlineStyles();
			case 'contained':
				return environment === 'light' ? lightStyles : darkStyles;
			default:
				return lightStyles;
		}
	};

	return (
		<button
			type={isSubmit ? 'submit' : 'button'}
			className={clsx(
				'flex cursor-pointer select-none items-center justify-center rounded-sm px-[22px] py-[14px] text-button',
				getVariantStyles(),
				classOverrides,
			)}
			onKeyDown={() => null}
			onClick={disabled || loading ? undefined : onClick}
			tabIndex={disabled || loading ? -1 : 0}
		>
			{children}
			{loading && <Image src={SpinIcon} width={24} height={24} alt="" className="ml-[20px] animate-spin" />}
		</button>
	);
};

export default Button;
